import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { fullKBReviewService } from '../services/fullKBReviewService';
import type { CostEstimate, ReviewLaunchResponse, ReviewProgress } from '../services/fullKBReviewService';

interface FullKBReviewState {
  reviewId: string | null;
  transcriptId: string | null;
  costEstimate: CostEstimate | null;
  progress: ReviewProgress | null;
  isLoadingEstimate: boolean;
  isLaunching: boolean;
  isPolling: boolean;
  error: string | null;
  // Poll timer lives in store state (not a module-level variable) so its
  // lifecycle is visible and reset/stop always clear the right handle.
  pollingTimer: ReturnType<typeof setInterval> | null;
}

interface FullKBReviewActions {
  fetchEstimate: (subProjectId: string, kbId: string) => Promise<void>;
  startReview: (subProjectId: string, kbId: string) => Promise<ReviewLaunchResponse>;
  startPolling: (subProjectId: string, reviewId: string, intervalMs?: number) => void;
  stopPolling: () => void;
  cancelReview: (subProjectId: string) => Promise<void>;
  reset: () => void;
}

const initialState: FullKBReviewState = {
  reviewId: null,
  transcriptId: null,
  costEstimate: null,
  progress: null,
  isLoadingEstimate: false,
  isLaunching: false,
  isPolling: false,
  error: null,
  pollingTimer: null,
};

export const useFullKBReviewStore = create<FullKBReviewState & FullKBReviewActions>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchEstimate: async (subProjectId, kbId) => {
        set({ isLoadingEstimate: true, error: null });
        try {
          const estimate = await fullKBReviewService.estimateCost(subProjectId, kbId);
          set({ costEstimate: estimate, isLoadingEstimate: false });
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : String(err);
          set({ error: msg, isLoadingEstimate: false });
          throw err;
        }
      },

      startReview: async (subProjectId, kbId) => {
        set({ isLaunching: true, error: null });
        try {
          const launch = await fullKBReviewService.launchReview(subProjectId, kbId);
          set({
            reviewId: launch.review_id,
            transcriptId: launch.transcript_id,
            isLaunching: false,
          });
          return launch;
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : String(err);
          set({ error: msg, isLaunching: false });
          throw err;
        }
      },

      startPolling: (subProjectId, reviewId, intervalMs = 3000) => {
        const prev = get().pollingTimer;
        if (prev) clearInterval(prev);

        let consecutiveFailures = 0;
        const poll = async () => {
          try {
            const progress = await fullKBReviewService.getProgress(subProjectId, reviewId);
            consecutiveFailures = 0;
            set({ progress });
            if (['completed', 'error', 'cancelled'].includes(progress.status)) {
              get().stopPolling();
            }
          } catch (_) {
            // Transient errors tolerated, but not forever: stop and surface after 5 in a row.
            consecutiveFailures += 1;
            if (consecutiveFailures >= 5) {
              get().stopPolling();
              set({ error: 'Progress updates failed repeatedly. Reopen this panel to retry.' });
            }
          }
        };

        poll();
        set({ isPolling: true, pollingTimer: setInterval(poll, intervalMs) });
      },

      stopPolling: () => {
        const timer = get().pollingTimer;
        if (timer) clearInterval(timer);
        set({ isPolling: false, pollingTimer: null });
      },

      cancelReview: async (subProjectId) => {
        const { reviewId } = get();
        if (!reviewId) return;
        try {
          await fullKBReviewService.cancelReview(subProjectId, reviewId);
        } catch (_) {
          // Best-effort cancel — do not surface errors to user
        }
      },

      reset: () => {
        const timer = get().pollingTimer;
        if (timer) clearInterval(timer);
        set(initialState);
      },
    }),
    { name: 'FullKBReviewStore' }
  )
);
